import React, { useState } from 'react';
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import { IconDownload } from '../components/Icons';

import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 10, padding: '10px 14px', fontSize: 12
    }}>
      <strong>{label}</strong>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, marginTop: 4 }}>
          {p.name === 'earnings' ? `₹${p.value.toLocaleString()}` : `${p.value} orders`}
        </div>
      ))}
    </div>
  );
};

export default function Reports() {
  const [period, setPeriod] = useState('weekly');
  const [orders, setOrders] = useState([]);

  React.useEffect(() => {
    const unsub = onSnapshot(collection(db, 'orders'), snap => {
      setOrders(snap.docs.map(d => ({id: d.id, ...d.data()})));
    });
    return () => unsub();
  }, []);

  // Compute stats from orders
  let totalEarnings = 0;
  let totalOrders = orders.length;

  const shopMap = {};
  const boyMap = {};
  const dateMap = {};

  orders.forEach(o => {
    const amt = parseInt(String(o.amount).replace(/[^0-9]/g, '')) || 0;
    if (o.status === 'Delivered') totalEarnings += amt;
    
    // Shop aggregation
    let shopName = 'Unknown Shop';
    if (typeof o.shop === 'string' && o.shop.trim()) shopName = o.shop.trim();
    else if (typeof o.shop === 'object' && o.shop !== null) shopName = o.shop.name || o.shop.shopName || o.shop.phone || 'Unknown Shop';
    else if (o.shopName) shopName = o.shopName;

    if (!shopMap[shopName]) shopMap[shopName] = { name: String(shopName), orders: 0, revenue: 0, cat: '🛒' };
    shopMap[shopName].orders += 1;
    shopMap[shopName].revenue += amt;

    // Delivery boy aggregation
    let boyName = 'Unassigned';
    if (typeof o.deliveryBoy === 'string' && o.deliveryBoy.trim()) boyName = o.deliveryBoy.trim();
    else if (typeof o.deliveryBoy === 'object' && o.deliveryBoy !== null) boyName = o.deliveryBoy.name || o.deliveryBoy.phone || 'Unassigned';
    else if (o.deliveryBoyName) boyName = o.deliveryBoyName;

    if (!boyMap[boyName]) boyMap[boyName] = { name: String(boyName), orders: 0, earned: 0, rating: 4.8 };
    boyMap[boyName].orders += 1;
    boyMap[boyName].earned += (o.deliveryFee || 20);

    // Date aggregation for charts
    if (o.placedAt && typeof o.placedAt.toDate === 'function') {
      const d = o.placedAt.toDate();
      const dateKey = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!dateMap[dateKey]) {
        dateMap[dateKey] = { 
          date: dateKey, orders: 0, earnings: 0, 
          shopShare: 0, deliveryShare: 0, platformShare: 0 
        };
      }

      if (o.status === 'Delivered') {
        const pShare = Math.round(amt * 0.1);
        const dShare = (o.deliveryFee || 20);
        const sShare = amt - pShare; // Shop gets item amount minus platform fee. Delivery is separate/extra usually, but we split the total.
        
        // Revised Split Logic:
        // totalCollected = amt (from table)
        // Platform gets 10%
        // Delivery Boy gets 20 (standard)
        // Shopkeeper gets the rest
        
        dateMap[dateKey].orders += 1;
        dateMap[dateKey].earnings += amt;
        dateMap[dateKey].platformShare += pShare;
        dateMap[dateKey].deliveryShare += dShare;
        dateMap[dateKey].shopShare += (amt - pShare - dShare);
      }
    }
  });

  const topShops = Object.values(shopMap).sort((a,b) => b.revenue - a.revenue).slice(0, 5);
  const topDelivery = Object.values(boyMap).filter(b => b.name !== 'Unassigned').sort((a,b) => b.earned - a.earned).slice(0, 5);
  
  let chartData = Object.values(dateMap);
  if (chartData.length === 0) chartData = [{ date: 'Today', month: 'Today', orders: 0, earnings: 0, avg: 0 }];

  const data = chartData;
  const exportToCSV = () => {
    if (orders.length === 0) {
      alert('एक्सपोर्ट करण्यासाठी डेटा उपलब्ध नाही.');
      return;
    }
    const headers = ['Order ID,Customer,Shop,Amount,Status,Delivery Fee,Date\n'];
    const rows = orders.map(o => {
      const id = o.id || '';
      const cust = `"${typeof o.customer === 'object' ? (o.customer?.name || o.customer?.phone || 'Customer') : (o.customerName || o.customer || 'Customer')}"`;
      const shop = `"${typeof o.shop === 'object' ? (o.shop?.name || 'Shop') : (o.shop || 'Shop')}"`;
      const amt = o.amount || 0;
      const status = o.status || 'Pending';
      const fee = o.deliveryFee || 20;
      const dateStr = o.placedAt?.seconds ? new Date(o.placedAt.seconds * 1000).toLocaleDateString() : '';
      return `${id},${cust},${shop},${amt},${status},${fee},${dateStr}`;
    });

    const blob = new Blob([headers + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Smart_Sangola_Orders_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const xKey = 'date';

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div className="section-title">कमाई अहवाल | Earnings Reports</div>
          <div className="section-subtitle">Smart Sangola Analytics</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {['weekly', 'monthly'].map(p => (
            <button key={p}
              className={`btn ${period === p ? 'btn-primary' : 'btn-secondary'} btn-sm`}
              onClick={() => setPeriod(p)}>
              {p === 'weekly' ? '📅 Weekly' : '📆 Monthly'}
            </button>
          ))}
          <button className="btn btn-secondary btn-sm" onClick={exportToCSV}>
            <IconDownload size={13} /> Export CSV
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { l: 'Total Earnings', v: `₹${totalEarnings.toLocaleString()}`, icon: '💰', c: 'var(--success)' },
          { l: 'Total Orders', v: totalOrders, icon: '📦', c: 'var(--info)' },
          { l: 'Avg Order Value', v: `₹${Math.round(totalEarnings / totalOrders)}`, icon: '📊', c: 'var(--warning)' },
          { l: 'Platform Commission (10%)', v: `₹${Math.round(totalEarnings * 0.1).toLocaleString()}`, icon: '🏦', c: 'var(--primary)' },
        ].map((s, i) => (
          <div key={i} className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.c }}>{s.v}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid-2 mb-24">
        <div className="card">
          <div className="card-header">
            <div className="card-title">💰 Earnings Trend</div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data}>
              <defs>
                <linearGradient id="repGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22C55E" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22C55E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey={xKey} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="earnings" stroke="#22C55E" strokeWidth={2.5}
                fill="url(#repGrad)" name="earnings" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">📦 Orders Volume</div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} barSize={20}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey={xKey} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="orders" fill="#FF6B35" radius={[4, 4, 0, 0]} name="orders" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tables */}
      <div className="grid-2">
        {/* Top Shops */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">🏆 Top Performing Shops</div>
          </div>
          <table>
            <thead>
              <tr><th>#</th><th>Shop</th><th>Orders</th><th>Revenue</th></tr>
            </thead>
            <tbody>
              {topShops.map((s, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 700, color: i < 3 ? ['#F59E0B','#94A3B8','#CD7F32'][i] : 'var(--text-muted)' }}>
                    {i + 1}
                  </td>
                  <td>
                    <span style={{ marginRight: 6 }}>{s.cat}</span>
                    {typeof s.name === 'object' ? (s.name?.name || s.name?.phone || 'Shop') : String(s.name || 'Shop')}
                  </td>
                  <td style={{ fontWeight: 600 }}>{s.orders}</td>
                  <td style={{ fontWeight: 700, color: 'var(--success)' }}>₹{s.revenue.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Top Delivery Boys */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">🏅 Top Delivery Boys</div>
          </div>
          <table>
            <thead>
              <tr><th>#</th><th>Name</th><th>Orders</th><th>Earned</th><th>Rating</th></tr>
            </thead>
            <tbody>
              {topDelivery.map((d, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 700, color: i < 3 ? ['#F59E0B','#94A3B8','#CD7F32'][i] : 'var(--text-muted)' }}>
                    {i + 1}
                  </td>
                  <td style={{ fontWeight: 600 }}>🛵 {typeof d.name === 'object' ? (d.name?.name || d.name?.phone || 'Rider') : String(d.name || 'Rider')}</td>
                  <td>{d.orders}</td>
                  <td style={{ fontWeight: 700, color: 'var(--success)' }}>₹{d.earned.toLocaleString()}</td>
                  <td>⭐ {d.rating}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily Earnings Table */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-header">
          <div className="card-title">📅 दैनिक कमाई तपशील | Daily Earnings Breakdown</div>
        </div>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>तारीख | Date</th>
                <th>ऑर्डर्स</th>
                <th>एकूण कमाई</th>
                <th style={{ color: '#3B82F6' }}>दुकानदार (Shop)</th>
                <th style={{ color: '#F59E0B' }}>डिलिव्हरी (Delivery)</th>
                <th style={{ color: '#22C55E' }}>ॲडमिन (Admin)</th>
              </tr>
            </thead>
            <tbody>
              {[...Object.values(dateMap)]
                .sort((a, b) => {
                  const dateA = new Date(a.date + ", " + new Date().getFullYear());
                  const dateB = new Date(b.date + ", " + new Date().getFullYear());
                  return dateB - dateA;
                })
                .map((d, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{d.date}</td>
                    <td>{d.orders}</td>
                    <td style={{ fontWeight: 700 }}>₹{d.earnings.toLocaleString()}</td>
                    <td style={{ fontWeight: 600, color: '#3B82F6' }}>₹{d.shopShare.toLocaleString()}</td>
                    <td style={{ fontWeight: 600, color: '#F59E0B' }}>₹{d.deliveryShare.toLocaleString()}</td>
                    <td style={{ fontWeight: 700, color: '#22C55E' }}>₹{d.platformShare.toLocaleString()}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
